import React, { useCallback, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import urlJoin from 'url-join';
import { useQuery } from '@apollo/react-hooks';
import {
  AnimatedScreen,
  PageHeader,
  DEFAULT_RESULTS_PER_PAGE,
  Paginate,
  PaginationType,
  EmptyTable,
  Table,
} from 'Shared/components';
import { AuthorizationsToSellType, formatAuthorizedStates } from 'Admin/utils';
import {
  FilterType,
  PartnerFilters,
} from 'Admin/Partners/components/PartnerFilters';
import { GET_PARTNERS_QUERY } from 'Admin/Partners/graphql';

type PartnersData = {
  partners?: {
    pagination: PaginationType;
    records: Array<{
      id: string;
      name: string;
      parent: {
        name: string;
      } | null;
      licensedAgency: {
        employerIdentificationNumber: string;
        nationalProducerNumber: string;
        authorizationsToSell: Array<AuthorizationsToSellType>;
      } | null;
    }>;
  };
};

export const PartnersHome: React.FC<RouteComponentProps> = ({ history }) => {
  const [resultsPerPage, changeResultsPerPage] = useState(
    DEFAULT_RESULTS_PER_PAGE
  );

  const [filters, setFilters] = useState<FilterType>({});

  const { networkStatus, error, data, fetchMore } = useQuery<PartnersData>(
    GET_PARTNERS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        paginate: {
          limit: resultsPerPage,
          offset: 0,
        },
        filters: filters,
      },
    }
  );

  const onChangeFilters = useCallback((newFilters: FilterType) => {
    setFilters(newFilters);
  }, []);

  const partners = data?.partners?.records;
  const pagination = data?.partners?.pagination;

  return (
    <AnimatedScreen
      testId="all-partners-home"
      loading={networkStatus === 1}
      error={error && error.message}
    >
      <PageHeader heading="Agencies" />

      <PartnerFilters filters={filters} onChangeFilters={onChangeFilters} />

      {partners && partners.length ? (
        <>
          <Table
            headers={['Agency Name', 'Parent', 'EIN', 'NPN', 'Active States']}
            columnWidths={[2, 2, 1, 1, 2]}
            name="all-partners-table"
            rows={partners.map(partner => ({
              key: partner.id,
              onViewRow: () =>
                history.push(urlJoin('/admin/partners/', partner.id)),
              data: [
                partner.name,
                partner.parent?.name,
                partner.licensedAgency?.employerIdentificationNumber,
                partner.licensedAgency?.nationalProducerNumber,
                partner.licensedAgency?.authorizationsToSell &&
                  formatAuthorizedStates(
                    partner.licensedAgency.authorizationsToSell
                  ),
              ],
            }))}
            loading={[2, 3, 4].includes(networkStatus)}
            hasPagination
          />
          <Paginate
            total={pagination?.total || 0}
            offset={pagination?.offset}
            limit={pagination?.limit}
            onOffsetChange={(limit, offset) => {
              fetchMore({
                variables: { paginate: { offset, limit } },
                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) return prev;
                  return {
                    ...prev,
                    ...fetchMoreResult,
                  };
                },
              });
            }}
            onLimitChange={changeResultsPerPage}
          />
        </>
      ) : (
        <EmptyTable>No existing system agencies</EmptyTable>
      )}
    </AnimatedScreen>
  );
};
